(() => {
  const ELEMENT_NAME = "header-mega-menu";
  const DESKTOP_MQ = "(min-width: 768px)";

  if (customElements.get(ELEMENT_NAME)) return;

  class HeaderMegaMenu extends HTMLElement {
    static ANIMATION_MS = 250;
    static OPEN_DELAY_MS = 180;
    static CLOSE_DELAY_MS = 250;
    static IMAGE_DEBOUNCE_MS = 200;
    // Figma prototype: Menu item MOUSE_ENTER -> Smart Animate, ease-out, 300ms.
    static PROMO_FADE_MS = 300;
    static PROMO_COPY_FADE_MS = 150;

    #root = null;
    #header = null;
    #nav = null;
    #indicator = null;
    #overlay = null;
    #panelsRegion = null;
    #panels = new Map();
    #triggers = new Map();
    #navLinks = [];
    #mediaByPanel = new Map();
    #activeId = null;
    #activeNavLink = null;
    #openTimer = 0;
    #closeTimer = 0;
    #imageTimer = 0;
    #promoFadeTimer = 0;
    #promoCopyTimer = 0;
    #promoSwapToken = 0;
    #panelAnimationTimer = 0;
    #desktopQuery = null;
    #boundOnNavLinkEnter = this.#onNavLinkEnter.bind(this);
    #boundOnNavLinkFocus = this.#onNavLinkFocus.bind(this);
    #boundOnPreviewEnter = this.#onPreviewEnter.bind(this);
    #boundOnPreviewLeave = this.#onPreviewLeave.bind(this);
    #boundOnRootLeave = this.#onRootLeave.bind(this);
    #boundOnRootEnter = this.#onRootEnter.bind(this);
    #boundOnPanelLeave = this.#onPanelLeave.bind(this);
    #boundOnNavLeave = this.#onNavLeave.bind(this);
    #boundOnKeydown = this.#onKeydown.bind(this);
    #boundOnDocumentFocusIn = this.#onDocumentFocusIn.bind(this);
    #boundOnDesktopChange = this.#onDesktopChange.bind(this);
    #boundOnResize = this.#onResize.bind(this);
    #boundOnScroll = this.#onScroll.bind(this);
    #boundOnOverlayEnter = this.#onOverlayEnter.bind(this);
    #boundOnOverlayClick = this.#onOverlayClick.bind(this);
    #boundOnDocumentPointerDown = this.#onDocumentPointerDown.bind(this);
    #onPromosLoaded = null;

    connectedCallback() {
      this.#root = this.closest("roka-header");
      this.#header = this.#root?.querySelector("[data-header]");
      this.#nav = this.#root?.querySelector("[data-header-nav]");
      this.#indicator = this.#root?.querySelector("[data-mega-menu-indicator]");
      this.#overlay = this.querySelector("[data-mega-menu-overlay]");
      this.#panelsRegion = this.querySelector("[data-mega-menu-panels]");
      this.#desktopQuery = window.matchMedia(DESKTOP_MQ);

      if (!this.#root || !this.#header) return;

      this.#collect();

      for (const link of this.#navLinks) {
        link.addEventListener("mouseenter", this.#boundOnNavLinkEnter);
        link.addEventListener("focus", this.#boundOnNavLinkFocus);
      }

      for (const panel of this.#panels.values()) {
        panel.addEventListener("mouseenter", this.#boundOnRootEnter);
        panel.addEventListener("mouseleave", this.#boundOnPanelLeave);
        for (const previewLink of panel.querySelectorAll(
          "[data-mega-menu-preview-image]",
        )) {
          previewLink.addEventListener("mouseenter", this.#boundOnPreviewEnter);
          previewLink.addEventListener("mouseleave", this.#boundOnPreviewLeave);
        }
      }

      this.#root.addEventListener("mouseleave", this.#boundOnRootLeave);
      this.#root.addEventListener("mouseenter", this.#boundOnRootEnter);
      this.#nav?.addEventListener("mouseleave", this.#boundOnNavLeave);
      this.#panelsRegion?.addEventListener(
        "mouseenter",
        this.#boundOnRootEnter,
      );
      this.#overlay?.addEventListener("mouseenter", this.#boundOnOverlayEnter);
      this.#overlay?.addEventListener("click", this.#boundOnOverlayClick);
      this.#desktopQuery.addEventListener("change", this.#boundOnDesktopChange);
      window.addEventListener("resize", this.#boundOnResize);
      window.addEventListener("scroll", this.#boundOnScroll, { passive: true });
      document.addEventListener("keydown", this.#boundOnKeydown);
      document.addEventListener("focusin", this.#boundOnDocumentFocusIn);
      document.addEventListener(
        "pointerdown",
        this.#boundOnDocumentPointerDown,
      );
      this.#onPromosLoaded = () => this.#refreshPromoMedia();
      document.addEventListener(
        "mega-menu-promos:loaded",
        this.#onPromosLoaded,
      );
    }

    disconnectedCallback() {
      for (const link of this.#navLinks) {
        link.removeEventListener("mouseenter", this.#boundOnNavLinkEnter);
        link.removeEventListener("focus", this.#boundOnNavLinkFocus);
      }

      for (const panel of this.#panels.values()) {
        panel.removeEventListener("mouseenter", this.#boundOnRootEnter);
        panel.removeEventListener("mouseleave", this.#boundOnPanelLeave);
        for (const previewLink of panel.querySelectorAll(
          "[data-mega-menu-preview-image]",
        )) {
          previewLink.removeEventListener(
            "mouseenter",
            this.#boundOnPreviewEnter,
          );
          previewLink.removeEventListener(
            "mouseleave",
            this.#boundOnPreviewLeave,
          );
        }
      }

      this.#root?.removeEventListener("mouseleave", this.#boundOnRootLeave);
      this.#root?.removeEventListener("mouseenter", this.#boundOnRootEnter);
      this.#nav?.removeEventListener("mouseleave", this.#boundOnNavLeave);
      this.#panelsRegion?.removeEventListener(
        "mouseenter",
        this.#boundOnRootEnter,
      );
      this.#overlay?.removeEventListener(
        "mouseenter",
        this.#boundOnOverlayEnter,
      );
      this.#overlay?.removeEventListener("click", this.#boundOnOverlayClick);
      this.#desktopQuery?.removeEventListener(
        "change",
        this.#boundOnDesktopChange,
      );
      window.removeEventListener("resize", this.#boundOnResize);
      window.removeEventListener("scroll", this.#boundOnScroll);
      document.removeEventListener("keydown", this.#boundOnKeydown);
      document.removeEventListener("focusin", this.#boundOnDocumentFocusIn);
      document.removeEventListener(
        "pointerdown",
        this.#boundOnDocumentPointerDown,
      );
      document.removeEventListener(
        "mega-menu-promos:loaded",
        this.#onPromosLoaded,
      );
      this.#clearOpenTimer();
      this.#clearCloseTimer();
      this.#clearImageTimer();
      this.#clearPromoFadeTimer();
      this.#clearPromoCopyTimer();
      this.#clearPanelAnimationTimer();
      this.#activeNavLink = null;
      this.#close(false);
    }

    #refreshPromoMedia() {
      for (const [id, panel] of this.#panels) {
        const region = panel.querySelector("[data-mega-menu-media='default']");

        this.#mediaByPanel.set(id, {
          region,
          image: region?.querySelector(".mega-menu-promo__image"),
          previous: null,
          title: region?.querySelector("[data-mega-menu-media-title]"),
          link: region?.querySelector("[data-mega-menu-media-link]"),
          cta: region?.querySelector("[data-mega-menu-media-cta]"),
        });

        for (const previewLink of panel.querySelectorAll(
          "[data-mega-menu-preview-image]",
        )) {
          if (previewLink.dataset.promoBound === "true") continue;

          previewLink.dataset.promoBound = "true";
          previewLink.addEventListener("mouseenter", this.#boundOnPreviewEnter);
          previewLink.addEventListener("mouseleave", this.#boundOnPreviewLeave);
        }
      }
    }

    #collect() {
      this.#panels.clear();
      this.#triggers.clear();
      this.#mediaByPanel.clear();
      this.#navLinks = [];

      for (const panel of this.querySelectorAll("[data-mega-menu-panel]")) {
        const id = panel.dataset.megaMenuPanel;

        if (!id) continue;

        this.#panels.set(id, panel);

        const region = panel.querySelector("[data-mega-menu-media='default']");

        this.#mediaByPanel.set(id, {
          region,
          image: region?.querySelector(".mega-menu-promo__image"),
          previous: null,
          title: region?.querySelector("[data-mega-menu-media-title]"),
          link: region?.querySelector("[data-mega-menu-media-link]"),
          cta: region?.querySelector("[data-mega-menu-media-cta]"),
        });
      }

      for (const link of this.#root.querySelectorAll(
        "[data-header-nav-link]",
      )) {
        this.#navLinks.push(link);

        const id = link.dataset.megaMenuTrigger;

        if (!id || !this.#panels.has(id)) continue;

        this.#triggers.set(id, link);
      }
    }

    #onDesktopChange() {
      if (!this.#desktopQuery?.matches) {
        this.#activeNavLink = null;
        this.#close();
      }
    }

    #onResize() {
      if (this.#activeNavLink) {
        this.#syncActiveIndicator(this.#activeNavLink);
      }

      if (this.#activeId) {
        this.#syncOverlayOffset();
      }
    }

    #onScroll() {
      if (!this.#activeId && !this.#activeNavLink) return;

      if (this.#activeNavLink) {
        this.#syncActiveIndicator(this.#activeNavLink);
      }

      if (this.#activeId) {
        this.#syncOverlayOffset();
      }
    }

    #onNavLinkEnter(event) {
      if (!this.#isDesktop()) return;

      const link = event.currentTarget;

      if (!(link instanceof HTMLElement)) return;

      this.#activateNavLink(link);
    }

    #onNavLinkFocus(event) {
      if (!this.#isDesktop()) return;

      const link = event.currentTarget;

      if (!(link instanceof HTMLElement)) return;

      // Keyboard focus should open immediately — delay is for pointer hover only.
      this.#activateNavLink(link, { immediate: true });
    }

    #activateNavLink(link, { immediate = false } = {}) {
      this.#clearCloseTimer();
      this.#activeNavLink = link;
      this.#showIndicator(link);

      const id = link.dataset.megaMenuTrigger;

      if (id && this.#panels.has(id)) {
        // Match live Roka: brief hover intent before first open; switch instantly
        // once a panel is already showing.
        if (immediate || this.#activeId) {
          this.#clearOpenTimer();
          this.#open(id);
          return;
        }

        this.#clearOpenTimer();
        this.#openTimer = window.setTimeout(() => {
          this.#openTimer = 0;

          if (this.#activeNavLink !== link) return;

          this.#open(id);
        }, HeaderMegaMenu.OPEN_DELAY_MS);
        return;
      }

      this.#clearOpenTimer();
      this.#close();
    }

    #onPreviewLeave() {
      if (!this.#isDesktop() || !this.#activeId) return;

      this.#clearImageTimer();
      this.#imageTimer = window.setTimeout(() => {
        this.#resetPreview(this.#activeId);
      }, HeaderMegaMenu.IMAGE_DEBOUNCE_MS);
    }

    #onPreviewEnter(event) {
      if (!this.#isDesktop() || !this.#activeId) return;

      const link = event.currentTarget;
      const media = this.#mediaByPanel.get(this.#activeId);

      if (!media?.region) return;

      this.#clearImageTimer();
      this.#imageTimer = window.setTimeout(() => {
        this.#applyPreview(media, link);
      }, HeaderMegaMenu.IMAGE_DEBOUNCE_MS);
    }

    #onRootEnter() {
      // Only cancel a pending close while a panel is open or an open is still
      // armed. Do not clear #activeNavLink here — keyboard focus on a leaf
      // link leaves exactly that state, and the stranded open-delay case is
      // already handled by the immediate clear in #scheduleClose.
      if (this.#activeId || this.#openTimer) {
        this.#clearCloseTimer();
      }
    }

    #onRootLeave(event) {
      if (!this.#isDesktop()) return;

      // Overlay sits inside the root, so leaving the panel onto it does not
      // fire this event — handled by #onOverlayEnter / #onPanelLeave.
      if (this.#isInsideMegaMenu(event.relatedTarget)) return;

      this.#scheduleClose();
    }

    #onPanelLeave(event) {
      if (!this.#isDesktop()) return;

      if (this.#isInsideMegaMenu(event.relatedTarget)) return;

      this.#scheduleClose();
    }

    #onNavLeave(event) {
      if (!this.#isDesktop()) return;

      // With a panel open, the pointer usually crosses header padding before
      // reaching the flyout — don't treat that as a leave.
      if (this.#activeId) return;

      const related = event.relatedTarget;

      // Gaps between links still count as nav. Logo / search / icons live under
      // [data-header] too, but must cancel the armed open delay — otherwise the
      // panel opens after the pointer has already left the trigger.
      if (related instanceof Element && this.#isMegaMenuFocusTarget(related)) {
        return;
      }

      this.#scheduleClose();
    }

    #onOverlayEnter() {
      if (!this.#isDesktop()) return;

      this.#scheduleClose();
    }

    #onKeydown(event) {
      if (event.key === "Escape") {
        if (!this.#activeId && !this.#activeNavLink) return;

        const trigger =
          (this.#activeId && this.#triggers.get(this.#activeId)) ||
          this.#activeNavLink;

        this.#activeNavLink = null;
        this.#close();
        trigger?.focus?.();
        return;
      }

      this.#handlePanelTab(event);
    }

    #handlePanelTab(event) {
      if (event.key !== "Tab" || !this.#isDesktop() || !this.#activeId) return;

      const panel = this.#panels.get(this.#activeId);
      const trigger = this.#triggers.get(this.#activeId);

      if (!panel || !trigger) return;

      const focusables = this.#getPanelFocusables(panel);

      if (focusables.length === 0) return;

      const first = focusables[0];
      const active = event.target;

      // Panels sit after Bag Finder / search / icons in the DOM. Skip those so
      // keyboard users can reach nested mega menu links from the trigger.
      if (!event.shiftKey && active === trigger) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        trigger.focus();
      }
    }

    #getPanelFocusables(panel) {
      return [
        ...panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.closest("[hidden]")) return false;
        if (element.getAttribute("aria-hidden") === "true") return false;

        if (typeof element.checkVisibility === "function") {
          return element.checkVisibility();
        }

        return true;
      });
    }

    #onDocumentFocusIn(event) {
      if (!this.#isDesktop()) return;
      if (!this.#activeId && !this.#activeNavLink) return;

      const target = event.target;

      if (!(target instanceof Element)) return;
      if (this.#isMegaMenuFocusTarget(target)) return;

      this.#activeNavLink = null;
      this.#close();
    }

    #onOverlayClick() {
      this.#activeNavLink = null;
      this.#close();
    }

    #isMegaMenuFocusTarget(target) {
      return Boolean(
        target.closest("[data-mega-menu-panel]") ||
        target.closest("[data-mega-menu-panels]") ||
        target.closest("[data-header-nav-link]") ||
        target.closest("[data-header-nav]"),
      );
    }

    #isInsideMegaMenu(target) {
      if (!(target instanceof Element)) return false;

      // Overlay is inside the mega-menu element but should still close it.
      if (target.closest("[data-mega-menu-overlay]")) return false;

      return Boolean(
        target.closest("[data-mega-menu-panel]") ||
        target.closest("[data-mega-menu-panels]") ||
        target.closest("[data-header]") ||
        target.closest("[data-header-nav-link]") ||
        target.closest("[data-header-nav]"),
      );
    }

    #onDocumentPointerDown(event) {
      if (!this.#isDesktop()) return;

      if (!this.#activeId && !this.#activeNavLink) return;

      const target = event.target;

      if (!(target instanceof Element)) return;

      // Match focus/hover “inside” checks so nav list/item chrome (gaps between
      // links) does not dismiss an open panel while the pointer is still in nav.
      if (this.#isMegaMenuFocusTarget(target)) return;

      this.#activeNavLink = null;
      this.#close();
    }

    #open(id) {
      if (!this.#isDesktop() || !this.#panels.has(id)) return;

      this.#clearOpenTimer();
      this.#clearCloseTimer();
      this.#clearPanelAnimationTimer();
      this.#clearImageTimer();

      const previousId = this.#activeId;
      const isSwitch = Boolean(previousId) && previousId !== id;
      const panel = this.#panels.get(id);

      if (!panel) return;

      this.#activeId = id;

      if (isSwitch) {
        this.#switchPanels(previousId, id);
      } else {
        for (const panelId of this.#panels.keys()) {
          if (panelId === id) continue;

          this.#hidePanel(panelId);
        }

        this.#showPanel(panel, { crossfade: false });
      }

      for (const [triggerId, trigger] of this.#triggers) {
        const isActive = triggerId === id;
        trigger.classList.toggle("header__nav-link--mega-active", isActive);
        trigger.setAttribute("aria-expanded", String(isActive));
      }

      this.#revealOverlay();
      this.#setDocumentScrollLock(true);
      this.#syncOverlayOffset();
      this.#resetPreview(id, { animate: false });
    }

    #switchPanels(fromId, toId) {
      const from = this.#panels.get(fromId);
      const to = this.#panels.get(toId);

      if (!to) return;

      for (const panelId of this.#panels.keys()) {
        if (panelId === fromId || panelId === toId) continue;

        this.#hidePanel(panelId);
      }

      if (this.#prefersReducedMotion() || !from) {
        this.#hidePanel(fromId);
        this.#showPanel(to, { crossfade: false, immediate: true });
        return;
      }

      from.classList.remove("mega-menu-panel--crossfade");
      from.classList.add("mega-menu-panel--exit");

      this.#showPanel(to, { crossfade: true });

      this.#panelAnimationTimer = window.setTimeout(() => {
        this.#panelAnimationTimer = 0;

        if (this.#activeId !== toId) return;

        from.classList.remove(
          "mega-menu-panel--visible",
          "mega-menu-panel--exit",
          "mega-menu-panel--crossfade",
        );
        from.setAttribute("hidden", "");
        to.classList.remove("mega-menu-panel--crossfade");
        this.#syncOverlayOffset();
      }, HeaderMegaMenu.ANIMATION_MS);
    }

    #showPanel(panel, { crossfade = false, immediate = false } = {}) {
      panel.classList.remove("mega-menu-panel--exit");
      panel.removeAttribute("hidden");

      if (crossfade) {
        panel.classList.add("mega-menu-panel--crossfade");
      } else {
        panel.classList.remove("mega-menu-panel--crossfade");
      }

      if (immediate || this.#prefersReducedMotion()) {
        panel.classList.add("mega-menu-panel--visible");
        return;
      }

      if (panel.classList.contains("mega-menu-panel--visible")) {
        return;
      }

      panel.getBoundingClientRect();
      requestAnimationFrame(() => {
        if (this.#panels.get(this.#activeId) === panel) {
          panel.classList.add("mega-menu-panel--visible");
        }
      });
    }

    #revealOverlay() {
      const wasOpen = this.classList.contains("header-mega-menu--open");
      const wasClosing = this.classList.contains("header-mega-menu--closing");

      this.classList.remove("header-mega-menu--closing");
      this.#overlay?.removeAttribute("hidden");
      this.#overlay?.setAttribute("aria-hidden", "false");

      if (!wasOpen || wasClosing) {
        this.#overlay?.getBoundingClientRect();
      }

      this.classList.add("header-mega-menu--open");
    }

    #scheduleClose() {
      this.#clearOpenTimer();
      this.#clearCloseTimer();

      // Open-delay hover with no panel yet: drop intent immediately. A delayed
      // close here can be cancelled by #onRootEnter (logo/search/icons) and
      // leave the pink indicator active with nothing open.
      if (!this.#activeId) {
        this.#activeNavLink = null;
        this.#hideIndicator();
        return;
      }

      this.#closeTimer = window.setTimeout(() => {
        this.#activeNavLink = null;
        this.#close();
      }, HeaderMegaMenu.CLOSE_DELAY_MS);
    }

    close(animate = true) {
      this.#activeNavLink = null;
      this.#close(animate);
    }

    #close(animate = true) {
      this.#clearOpenTimer();
      this.#clearImageTimer();
      this.#clearPromoFadeTimer();
      this.#clearPromoCopyTimer();
      this.#promoSwapToken += 1;

      if (!this.#activeId) {
        this.#overlay?.setAttribute("hidden", "");
        this.#overlay?.setAttribute("aria-hidden", "true");
        this.classList.remove(
          "header-mega-menu--open",
          "header-mega-menu--closing",
        );
        this.#setDocumentScrollLock(false);

        if (!this.#activeNavLink) {
          this.#hideIndicator();
        }

        return;
      }

      const closingId = this.#activeId;

      this.#clearPanelAnimationTimer();
      this.classList.add("header-mega-menu--closing");

      for (const panelId of this.#panels.keys()) {
        if (panelId === closingId) continue;

        this.#hidePanel(panelId);
      }

      if (animate && !this.#prefersReducedMotion()) {
        this.#hidePanel(closingId, true);
        this.#panelAnimationTimer = window.setTimeout(() => {
          this.#panelAnimationTimer = 0;

          const panel = this.#panels.get(closingId);

          if (panel && !panel.classList.contains("mega-menu-panel--visible")) {
            panel.setAttribute("hidden", "");
            panel.classList.remove(
              "mega-menu-panel--exit",
              "mega-menu-panel--crossfade",
            );
          }

          if (this.#activeId === closingId) {
            this.#finishClose();
          }
        }, HeaderMegaMenu.ANIMATION_MS);
        return;
      }

      this.#hidePanel(closingId, false);
      this.#finishClose();
    }

    #hidePanel(id, animate = false) {
      const panel = this.#panels.get(id);

      if (!panel) return;

      panel.classList.remove("mega-menu-panel--visible");

      if (animate && !this.#prefersReducedMotion()) {
        return;
      }

      panel.setAttribute("hidden", "");
      panel.classList.remove(
        "mega-menu-panel--exit",
        "mega-menu-panel--crossfade",
      );
    }

    #finishClose() {
      for (const trigger of this.#triggers.values()) {
        trigger.classList.remove("header__nav-link--mega-active");
        trigger.setAttribute("aria-expanded", "false");
      }

      this.#activeId = null;
      this.#overlay?.setAttribute("hidden", "");
      this.#overlay?.setAttribute("aria-hidden", "true");
      this.classList.remove(
        "header-mega-menu--open",
        "header-mega-menu--closing",
      );
      this.#setDocumentScrollLock(false);
      this.#clearImageTimer();

      if (!this.#activeNavLink) {
        this.#hideIndicator();
      }
    }

    #setDocumentScrollLock(locked) {
      const root = document.documentElement;
      const lockClass = "header-mega-menu-open";
      const otherLockClass = "header-menu-open";

      if (locked) {
        if (
          !root.classList.contains(lockClass) &&
          !root.classList.contains(otherLockClass)
        ) {
          const compensation = Math.max(
            0,
            window.innerWidth - root.clientWidth,
          );
          root.style.setProperty(
            "--scrollbar-compensation",
            `${compensation}px`,
          );
        }

        root.classList.add(lockClass);
        return;
      }

      root.classList.remove(lockClass);

      if (!root.classList.contains(otherLockClass)) {
        root.style.removeProperty("--scrollbar-compensation");
      }
    }

    #showIndicator(link) {
      if (this.#header?.classList.contains("header--compact")) {
        this.#hideIndicator();
        return;
      }

      this.#syncActiveIndicator(link);
      this.#header?.classList.add("header--nav-indicator-active");
    }

    #hideIndicator() {
      this.#header?.classList.remove("header--nav-indicator-active");
      this.#header?.style.removeProperty("--mega-menu-indicator-left");
      this.#header?.style.removeProperty("--mega-menu-indicator-width");
    }

    #resetPreview(id, { animate = true } = {}) {
      const media = this.#mediaByPanel.get(id);

      if (!media?.region) return;

      const region = media.region;

      this.#swapPromo(
        media,
        {
          image: region.dataset.megaMenuDefaultImage || "",
          alt: region.dataset.megaMenuDefaultAlt || "",
          url: region.dataset.megaMenuDefaultUrl || "",
          title: region.dataset.megaMenuDefaultTitle || "",
          cta: region.dataset.megaMenuDefaultCta || "",
        },
        { animate },
      );
    }

    #applyPreview(media, link) {
      this.#swapPromo(media, {
        image: link.dataset.megaMenuPreviewImage || "",
        alt: link.dataset.megaMenuPreviewAlt || "",
        url: link.dataset.megaMenuPreviewUrl || "",
        title: link.dataset.megaMenuPreviewTitle || "",
        // Link previews have no matching CTA copy — hide the default promo CTA.
        cta: "",
      });
    }

    // Replicates the Figma prototype's Smart Animate: the outgoing frame is
    // parked on its own layer and fades down while the incoming image fades up,
    // so the tile crossfades instead of dipping through the background.
    #swapPromo(media, values, { animate = true } = {}) {
      const region = media.region;

      if (!region) return;

      this.#clearPromoFadeTimer();
      this.#clearPromoCopyTimer();
      this.#promoSwapToken += 1;

      if (!animate || this.#prefersReducedMotion()) {
        this.#setPromoImage(media, values.image, values.alt);
        this.#applyPromoCopy(media, values);
        this.#endPromoSwap(media);
        return;
      }

      const image = this.#ensurePromoImage(media);
      const previousSrc = image?.getAttribute("src") || "";
      const token = this.#promoSwapToken;

      // Copy has no second layer to crossfade against, so it swaps at the
      // midpoint of the same envelope the image crossfade runs on.
      region.classList.add("mega-menu-promo--swapping");
      this.#promoCopyTimer = window.setTimeout(() => {
        this.#promoCopyTimer = 0;

        if (token !== this.#promoSwapToken) return;

        this.#applyPromoCopy(media, values);
        region.classList.remove("mega-menu-promo--swapping");
      }, HeaderMegaMenu.PROMO_COPY_FADE_MS);

      // Same artwork already showing — only the copy needs to change. A
      // crossfade onto this same artwork may still be mid-flight, and the token
      // bump above has just orphaned its reveal, so settle the image layers
      // here rather than leaving the outgoing frame parked on top.
      if (!image || previousSrc === values.image) {
        this.#settlePromoImageLayers(media);
        return;
      }

      this.#crossfadePromoImage(media, image, previousSrc, values, token);
    }

    #crossfadePromoImage(media, image, previousSrc, values, token) {
      const previous = this.#ensurePromoPrevious(media);

      if (previous && previousSrc) {
        previous.src = previousSrc;
        this.#setOpacityWithoutTransition(previous, "1");
        previous.hidden = false;
      }

      // The parked layer hides this, so dropping to 0 must not itself animate.
      this.#setOpacityWithoutTransition(image, "0");
      this.#setPromoImage(media, values.image, values.alt);

      const reveal = () => {
        if (token !== this.#promoSwapToken) return;

        image.style.opacity = "1";

        if (previous) {
          previous.style.opacity = "0";
        }

        this.#clearPromoFadeTimer();
        this.#promoFadeTimer = window.setTimeout(() => {
          this.#promoFadeTimer = 0;

          if (token !== this.#promoSwapToken) return;

          this.#endPromoSwap(media);
        }, HeaderMegaMenu.PROMO_FADE_MS);
      };

      if (!values.image || image.complete) {
        window.requestAnimationFrame(reveal);
        return;
      }

      image.addEventListener("load", reveal, { once: true });
      image.addEventListener("error", reveal, { once: true });
      // Safety net — a stalled download must not strand the outgoing frame.
      this.#promoFadeTimer = window.setTimeout(
        reveal,
        HeaderMegaMenu.PROMO_FADE_MS * 3,
      );
    }

    #setOpacityWithoutTransition(element, value) {
      element.style.transition = "none";
      element.style.opacity = value;
      // Flush the change so the next assignment starts a fresh transition.
      void element.offsetWidth;
      element.style.removeProperty("transition");
    }

    #endPromoSwap(media) {
      media.region?.classList.remove("mega-menu-promo--swapping");
      this.#settlePromoImageLayers(media);
    }

    // Drops the live image back to its resting opacity and unparks the outgoing
    // frame. Safe to call when no crossfade ran — both are then no-ops.
    #settlePromoImageLayers(media) {
      const image = media.image;

      if (image) {
        // Snap rather than clear: the artwork is already correct, so letting
        // the 300ms transition run 0 -> 1 while the parked frame disappears
        // would dip the tile through the background.
        if (image.style.opacity && image.style.opacity !== "1") {
          this.#setOpacityWithoutTransition(image, "1");
        }

        image.style.removeProperty("opacity");
      }

      if (!media.previous) return;

      media.previous.hidden = true;
      media.previous.removeAttribute("src");
      media.previous.style.removeProperty("opacity");
    }

    #ensurePromoPrevious(media) {
      if (media.previous) return media.previous;

      const image = media.image;

      if (!image?.parentNode) return null;

      const previous = document.createElement("img");
      previous.className =
        "mega-menu-promo__image mega-menu-promo__image--previous";
      previous.alt = "";
      previous.setAttribute("aria-hidden", "true");
      previous.hidden = true;

      // After the live image but before the copy, so it covers the incoming
      // frame without ever painting over the title or CTA.
      image.after(previous);

      media.previous = previous;
      return previous;
    }

    #applyPromoCopy(media, values) {
      if (media.title && values.title) {
        media.title.textContent = values.title;
      }

      if (media.link && values.url) {
        media.link.href = values.url;
      }

      this.#setPromoCta(media, values.cta);
    }

    #setPromoCta(media, label) {
      if (!media.cta) return;

      if (label) {
        media.cta.textContent = label;
        media.cta.hidden = false;
        return;
      }

      media.cta.hidden = true;
    }

    #setPromoImage(media, src, alt = "") {
      const image = this.#ensurePromoImage(media);

      if (!image) return;

      const placeholder = media.region?.querySelector(
        ".mega-menu-promo__placeholder",
      );

      // Preview/default URLs are single sources; clear responsive attrs so the
      // browser does not keep serving the previous image_tag srcset candidate.
      image.removeAttribute("srcset");
      image.removeAttribute("sizes");

      if (src) {
        image.src = src;
        image.alt = alt;
        image.hidden = false;
        placeholder?.setAttribute("hidden", "");
        return;
      }

      image.removeAttribute("src");
      image.alt = alt;
      image.hidden = true;
      placeholder?.removeAttribute("hidden");
    }

    #ensurePromoImage(media) {
      if (media.image) return media.image;

      const mediaEl = media.region?.querySelector(".mega-menu-promo__media");

      if (!mediaEl) return null;

      const image = document.createElement("img");
      image.className = "mega-menu-promo__image";
      image.loading = "lazy";
      image.hidden = true;

      const placeholder = mediaEl.querySelector(
        ".mega-menu-promo__placeholder",
      );

      if (placeholder) {
        placeholder.before(image);
      } else {
        mediaEl.prepend(image);
      }

      media.image = image;
      return image;
    }

    #syncOverlayOffset() {
      if (!this.#header || !this.#overlay) return;

      // Full-viewport scrim from the header bottom — not from the panel bottom.
      // Anchoring below the panel left a gap while the flyout was still sliding
      // (offsetHeight is full height before transform catches up visually).
      const overlayTop = this.#header.getBoundingClientRect().bottom;

      this.style.setProperty(
        "--header-mega-menu-overlay-top",
        `${overlayTop}px`,
      );
    }

    #syncActiveIndicator(link) {
      if (!this.#header || !this.#indicator || !(link instanceof Element)) {
        return;
      }

      // Indicator lives in header__inner so stacked search does not shift it.
      const rail =
        this.#indicator.offsetParent instanceof Element
          ? this.#indicator.offsetParent
          : this.#header;
      const railRect = rail.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();

      this.#header.style.setProperty(
        "--mega-menu-indicator-left",
        `${linkRect.left - railRect.left}px`,
      );
      this.#header.style.setProperty(
        "--mega-menu-indicator-width",
        `${linkRect.width}px`,
      );
    }

    #isDesktop() {
      return Boolean(this.#desktopQuery?.matches);
    }

    #prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    #clearOpenTimer() {
      if (!this.#openTimer) return;

      window.clearTimeout(this.#openTimer);
      this.#openTimer = 0;
    }

    #clearCloseTimer() {
      if (!this.#closeTimer) return;

      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    }

    #clearImageTimer() {
      if (!this.#imageTimer) return;

      window.clearTimeout(this.#imageTimer);
      this.#imageTimer = 0;
    }

    #clearPromoFadeTimer() {
      if (!this.#promoFadeTimer) return;

      window.clearTimeout(this.#promoFadeTimer);
      this.#promoFadeTimer = 0;
    }

    #clearPromoCopyTimer() {
      if (!this.#promoCopyTimer) return;

      window.clearTimeout(this.#promoCopyTimer);
      this.#promoCopyTimer = 0;
    }

    #clearPanelAnimationTimer() {
      if (!this.#panelAnimationTimer) return;

      window.clearTimeout(this.#panelAnimationTimer);
      this.#panelAnimationTimer = 0;
    }
  }

  customElements.define(ELEMENT_NAME, HeaderMegaMenu);
})();
